// src/components/resources/tools/generators/EmailThread/GmailMessageRow.tsx

'use client'

import { memo } from 'react'
import { MoreVertical, Star } from 'lucide-react'
import { clsx } from 'clsx'
import { getAvatarStyle } from './email-thread-utils'
import type { GmailMessageRowProps } from './types'

/**
 * A single Gmail-style message row. Extracted and wrapped in `React.memo`
 * so that editing one message (or any thread-level field) re-renders only
 * the row whose `message` object actually changed — not every styled
 * bubble in the thread. `updateMessage` rebuilds `messages` with
 * `m.id === id ? { ...m } : m`, so unchanged messages keep their object
 * reference and this memo bails out of re-rendering them. `onToggleStar`
 * must be a stable (parent-hoisted) callback or the memo is defeated.
 */
export const GmailMessageRow = memo(function GmailMessageRow({
  message,
  isLast,
  showAvatars,
  highlightReply,
  onToggleStar,
}: GmailMessageRowProps): JSX.Element {
  return (
    <div className="group">
      <div className="flex items-start gap-3">
        {showAvatars && (
          <div
            className={clsx(
              "h-10 w-10 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-medium border",
              getAvatarStyle('gmail', message.isCurrentUser),
              message.isCurrentUser
                ? "border-blue-500"
                : "border-gray-300"
            )}
            title={message.isCurrentUser ? "You" : message.sender}
            aria-label={message.isCurrentUser ? "Your avatar" : `${message.sender} avatar`}
          >
            {message.sender.charAt(0)}
          </div>
        )}
        <div className={clsx(
          "flex-1 min-w-0",
          highlightReply && message.isCurrentUser ? "bg-blue-50 dark:bg-blue-900/30 rounded-lg px-3 py-2" : ""
        )}>
          <div className="flex items-baseline justify-between">
            <div className="flex items-baseline gap-2">
              <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{message.sender}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">&lt;{message.email}&gt;</span>
              {message.isCurrentUser && (
                <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-semibold">You</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">{message.timestamp}</span>
              <button
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                onClick={() => onToggleStar(message.id)}
                aria-label={message.starred ? "Unstar message" : "Star message"}
              >
                <Star className={clsx(
                  'h-4 w-4',
                  message.starred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400 dark:text-gray-500'
                )} />
              </button>
              <button className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="More message actions">
                <MoreVertical className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              </button>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {message.content}
          </div>
          {!isLast && (
            <div className="mt-3 flex gap-2">
              <button className="text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 px-3 py-1 rounded" aria-label="Reply">
                Reply
              </button>
              <button className="text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 px-3 py-1 rounded" aria-label="Forward">
                Forward
              </button>
            </div>
          )}
        </div>
      </div>
      {!isLast && (
        <div className="ml-12 mt-4 border-b border-gray-100 dark:border-gray-800" aria-hidden="true" />
      )}
    </div>
  )
})
